import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import {
  Navigate,
  createBrowserRouter,
  RouterProvider,
} from 'react-router-dom';
import AuthGuard from './guards/AuthGuard';
import UnauthGuard from './guards/UnauthGuard';
import AuthLayout from './layouts/AuthLayout';
import UnauthLayout from './layouts/UnauthLayout';
import ExpertConsultsDetail from './pages/Expert/ExpertConsultsDetail';

const App = lazy(() => import('./app'));
// The previous home (pages/Home) and the Resources page (pages/Resources)
// are kept but no longer routed: their content moved to MainHome
const MainHome = lazy(() => import('./pages/MainHome'));
const Network = lazy(() => import('./pages/Network'));
const Profile = lazy(() => import('./pages/Profile'));
const ProfileEdit = lazy(() => import('./pages/ProfileEdit'));
const RewardPrizes = lazy(() => import('./pages/RewardPrizes'));

const SignIn = lazy(() => import('./pages/Auth/SignIn'));
const SignUp = lazy(() => import('./pages/Auth/SignUp'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const Pending = lazy(() => import('./pages/Auth/Pending'));
const Waiting = lazy(() => import('./pages/Auth/Waiting'));

const Chat = lazy(() => import('./pages/Chat'));
const Consultations = lazy(() => import('./pages/Consultations'));
const Articles = lazy(() => import('./pages/Articles'));
const ExpertNetwork = lazy(() => import('./pages/ExpertNetwork'));
const ExpertNetworkRouter = lazy(
  () => import('./pages/ExpertNetwork/ExpertNetworkRouter')
);
const Collective = lazy(() => import('./pages/Collectives'));
const CollectivesRouter = lazy(
  () => import('./pages/Collectives/CollectivesRouter')
);
const Mentors = lazy(() => import('./pages/Mentors'));
const MentorsRouter = lazy(() => import('./pages/Mentors/MentorsRouter'));
const BackgroundChecks = lazy(() => import('./pages/BackgroundChecks'));
const ExclusiveDeals = lazy(() => import('./pages/ExclusiveDeals'));
const DealPage = lazy(
  () => import('./pages/ExclusiveDeals/ExclusiveDealDetail')
);
const ExpertConsults = lazy(() => import('./pages/Expert/ExpertConsults'));

const SchoolSurveyScreen = lazy(() => import('./pages/SchoolSurvey'));
const SchoolSurveyResults = lazy(
  () => import('./pages/SchoolSurvey/SchoolSurveyResults')
);

const Discounts = lazy(() => import('./pages/Discounts'));
const DiscountsDetail = lazy(() => import('./pages/Discounts/DiscountsDetail'));

const SurveyPreview = lazy(() => import('./pages/Survey/SurveyPreview'));

const DocumentReview = lazy(() => import('./pages/DocumentReview'));

const NotFound = lazy(() => import('./pages/NotFound'));

const routes: RouteObject[] = [
  {
    path: '',
    element: (
      <AuthGuard>
        <AuthLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '/',
        element: (
          <Suspense>
            <MainHome />
          </Suspense>
        ),
      },
      {
        path: '/profile',
        element: (
          <Suspense>
            <Profile />
          </Suspense>
        ),
      },
      {
        path: '/profile/edit',
        element: (
          <Suspense>
            <ProfileEdit />
          </Suspense>
        ),
      },
      {
        path: '/profile/reward-prizes',
        element: (
          <Suspense>
            <RewardPrizes />
          </Suspense>
        ),
      },
      {
        path: '/resources',
        element: <Navigate to="/" replace />,
      },
      {
        path: '/network',
        element: (
          <Suspense>
            <Network />
          </Suspense>
        ),
      },
      {
        path: 'resources/chat',
        element: (
          <Suspense>
            <Chat />
          </Suspense>
        ),
      },
      {
        path: 'resources/document-review',
        element: (
          <Suspense>
            <DocumentReview />
          </Suspense>
        ),
      },
      {
        path: '/consultations',
        element: (
          <Suspense>
            <Consultations />
          </Suspense>
        ),
      },
      {
        path: '/network/expert-network',
        element: (
          <Suspense>
            <ExpertNetwork />
          </Suspense>
        ),
      },
      {
        path: '/network/expert-network/:uuid',
        element: (
          <Suspense>
            <ExpertNetworkRouter />
          </Suspense>
        ),
      },
      {
        path: '/network/agencies-collectives',
        element: (
          <Suspense>
            <Collective />
          </Suspense>
        ),
      },
      {
        path: '/network/agencies-collectives/:uuid',
        element: (
          <Suspense>
            <CollectivesRouter />
          </Suspense>
        ),
      },
      {
        path: '/network/mentors',
        element: (
          <Suspense>
            <Mentors />
          </Suspense>
        ),
      },
      {
        path: '/network/mentors/:uuid',
        element: (
          <Suspense>
            <MentorsRouter />
          </Suspense>
        ),
      },
      {
        path: '/resources/background-checks',
        element: (
          <Suspense>
            <BackgroundChecks />
          </Suspense>
        ),
      },

      {
        path: '/resources/exclusive-deals',
        element: (
          <Suspense>
            <ExclusiveDeals />
          </Suspense>
        ),
      },
      {
        path: 'resources/exclusive-deals/:deal_key',
        element: (
          <Suspense>
            <DealPage />
          </Suspense>
        ),
      },
      {
        path: '/articles',
        element: (
          <Suspense>
            <Articles />
          </Suspense>
        ),
      },

      {
        path: '/expert/consultations',
        element: (
          <Suspense>
            <ExpertConsults />
          </Suspense>
        ),
      },
      {
        path: '/expert/consultations/detail',
        element: (
          <Suspense>
            <ExpertConsultsDetail />
          </Suspense>
        ),
      },
      {
        path: '/expert/consultations/:uuid/detail',
        element: (
          <Suspense>
            <ExpertConsultsDetail />
          </Suspense>
        ),
      },
      {
        path: '/survey/school-ratings',
        element: (
          <Suspense>
            <SchoolSurveyScreen />
          </Suspense>
        ),
      },
      {
        path: '/survey/school-ratings/results',
        element: (
          <Suspense>
            <SchoolSurveyResults />
          </Suspense>
        ),
      },
      {
        path: '/discounts',
        element: (
          <Suspense>
            <Discounts />
          </Suspense>
        ),
      },
      {
        path: '/discounts/:uuid',
        element: (
          <Suspense>
            <DiscountsDetail />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '',
    element: (
      <UnauthGuard>
        <UnauthLayout />
      </UnauthGuard>
    ),
    children: [
      {
        path: '/sign-in',
        element: (
          <Suspense>
            <SignIn />
          </Suspense>
        ),
      },
      {
        path: '/welcome',
        element: (
          <Suspense>
            {/* <WelcomeScreen /> */}
            <SignIn />
          </Suspense>
        ),
      },

      {
        path: '/sign-up',
        element: (
          <Suspense>
            <SignUp />
          </Suspense>
        ),
      },
      {
        path: '/sign-up/:slug',
        element: (
          <Suspense>
            <SignUp />
          </Suspense>
        ),
      },
      {
        path: '/reset-password/:token',
        element: (
          <Suspense>
            <ResetPassword />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense>
        <NotFound />
      </Suspense>
    ),
  },
  {
    path: '/pending',
    element: (
      <Suspense>
        <Pending />
      </Suspense>
    ),
  },
  {
    path: '/waiting',
    element: (
      <Suspense>
        <Waiting />
      </Suspense>
    ),
  },
  {
    path: '/survey/preview/:uuid',
    element: (
      <Suspense>
        <SurveyPreview />
      </Suspense>
    ),
  },
];

const router = createBrowserRouter([
  {
    path: '',
    element: (
      <Suspense>
        <App />
      </Suspense>
    ),
    children: routes,
  },
]);

function AppRoutes() {
  return <RouterProvider router={router} />;
}

export default AppRoutes;
